import lodash, { some } from 'lodash'
import React, { useEffect, useMemo } from 'react'
import { ReactElement } from 'react'

import Field, {
  FieldPrefix,
  Label,
  useField,
} from '../../../../../../../1stlog-ui/src/components/Field'
import useFieldValue from '../../../../../../../1stlog-ui/src/components/Field/useFieldValue'
import { useFieldArray } from '../../../../../../../1stlog-ui/src/components/FieldArray'
import Icon from '../../../../../../../1stlog-ui/src/components/Icon'
import Overlay from '../../../../../../../1stlog-ui/src/components/Overlay'
import ToggleSwitch from '../../../../../../../1stlog-ui/src/components/ToggleSwitch'
import useOrderEntry from '../../../../useOrderEntry'
import { useServiceDeactivation } from '../../../Services/useServiceDeactivation'
import ServiceOption from './ServiceOption'

const HIDDEN_SERVICES: string[] = [] // 'DIS'

type TShipmentServiceProps = {
  readonly?: boolean
  hiddenServices?: string[]
}

const ShipmentService = (props: TShipmentServiceProps): ReactElement => {
  const { readonly, hiddenServices } = props

  const { customServiceComponents } = useOrderEntry()

  const name = useFieldValue<string>('name')

  const label = useField('label')

  const isSuccess = useField('isSuccess')
  const isError = useField('isError')

  useServiceDeactivation()

  const isEnabledField = useField('isEnabled')

  const isEnabled = useMemo(() => isEnabledField, [isEnabledField])

  const { fields } = useFieldArray('values')
  const [values, setValues] = useField('values', { asState: true })

  const isBoolToggle = useMemo(
    () =>
      lodash.size(values) === 1 &&
      lodash.get(lodash.first(values), 'type') === 'bool',
    [values],
  )

  useEffect(() => {
    if (isBoolToggle) {
      setValues(lodash.map(values, (value) => ({ ...value, value: isEnabled })))
    }
  }, [isBoolToggle, isEnabled])

  const hiddenIfOwnAddress = useFieldValue('hiddenIfOwnAddress')
  const isOwnDeliveryAddress = useFieldValue('delivery.isOwnDeliveryAddress', {
    ignorePrefix: true,
  })

  const hidden =
    (readonly && (!isEnabled || HIDDEN_SERVICES.includes(name))) ||
    hiddenServices?.includes(name) ||
    (isOwnDeliveryAddress && hiddenIfOwnAddress)

  const shipmentServices = useFieldValue<any[]>('shipmentServices', {
    ignorePrefix: true,
  })

  const isDisabled = useMemo(() => {
    return some(shipmentServices, (service) => {
      return service.isEnabled && service.disallows?.includes(name)
    })
  }, [shipmentServices])

  return (
    <div
      className="mb-1"
      hidden={Boolean(hidden)}
      style={{ position: 'relative' }}
    >
      {isDisabled && <Overlay />}
      <div>
        {readonly ? (
          <div>
            {isError && <Icon icon="fas fa-times" danger className="mr-1" />}
            {isSuccess && <Icon icon="fas fa-check" success className="mr-1" />}
            {!isError && !isSuccess && (
              <Icon icon="fas fa-check" className="mr-1" />
            )}{' '}
            <Label>{label}</Label>
          </div>
        ) : (
          <Field name="isEnabled" noMargin>
            <ToggleSwitch label={label} id={name} />
          </Field>
        )}
      </div>

      {isEnabled && (
        <>
          <div className="ml-4">
            <div>
              {fields.map((optionName: string) => (
                <FieldPrefix name={optionName} key={optionName} overwritePrefix>
                  <ServiceOption readonly={readonly} serviceName={name} />
                </FieldPrefix>
              ))}
            </div>
            {customServiceComponents && customServiceComponents[name] ? (
              React.cloneElement(customServiceComponents[name], {
                isEdit: !readonly,
              })
            ) : (
              <></>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default ShipmentService
